import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button, Modal } from "../../components/ui";
import type { WorkflowLibraryCard, WorkflowLibraryView } from "../../components/NoteWorkflows/workflowLibraryTypes";
import browserStyles from "../../components/NoteWorkflows/NoteWorkflows.module.css";
import { useBuilderIdentity } from "../workflowBuilder/useBuilderIdentity";
import builderStyles from "../workflowBuilder/WorkflowBuilder.module.css";
import reminderStyles from "../workflowBuilder/ReminderTask.module.css";
import { deleteShiftHandoverRule, enableShiftHandoverRule, listShiftHandoverRules } from "./handoverApi";
import type { ShiftHandoverRule } from "./handoverModels";
import styles from "./ShiftHandovers.module.css";

type Props = { organizationId: string; onEdit: (id: string) => void; onCreate: () => void;
    renderLibrary?: (library: WorkflowLibraryView) => ReactNode };
export function ShiftHandoverLibrary({ organizationId, onEdit, onCreate, renderLibrary }: Props) {
    const { resolveToken } = useBuilderIdentity();
    const [items, setItems] = useState<ShiftHandoverRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0);
    const [busy, setBusy] = useState(false);
    const [deleting, setDeleting] = useState<ShiftHandoverRule | null>(null);
    const mutation = useRef<AbortController | null>(null);
    const inFlight = useRef(false);
    useEffect(() => {
        const abort = new AbortController();
        setLoading(true); setError("");
        void resolveToken().then(token => listShiftHandoverRules(organizationId, token, abort.signal)).then(result => {
            if (!abort.signal.aborted) setItems(result);
        }).catch((cause: unknown) => { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : "Unable to load shift handovers."); })
            .finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [organizationId, resolveToken, attempt]);
    useEffect(() => () => mutation.current?.abort(), []);
    const change = async (rule: ShiftHandoverRule, remove: boolean) => {
        if (inFlight.current) return;
        const abort = new AbortController(); mutation.current = abort; inFlight.current = true; setBusy(true); setError("");
        try {
            const token = await resolveToken();
            if (abort.signal.aborted) return;
            if (remove) {
                await deleteShiftHandoverRule(organizationId, rule, token, abort.signal);
                if (!abort.signal.aborted) { setItems(current => current.filter(item => item.id !== rule.id)); setDeleting(null); }
            } else {
                const updated = await enableShiftHandoverRule(organizationId, rule, !rule.enabled, token, abort.signal);
                if (!abort.signal.aborted) setItems(current => current.map(item => item.id === updated.id ? updated : item));
            }
        } catch (cause: unknown) { if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : "Unable to update this handover."); }
        finally { if (!abort.signal.aborted) { inFlight.current = false; setBusy(false); } }
    };
    const feedback = <>
        {error && <p className={styles.error} role="alert">{error} <Button size="sm" disabled={busy || loading} onClick={() => setAttempt(value => value + 1)}>Refresh</Button></p>}
        {loading && <p role="status">Loading…</p>}
    </>;
    const cards: WorkflowLibraryCard[] = items.map(rule => {
        const timing = `${rule.definition.lead_minutes} min before each shift · Last ${rule.definition.lookback_hours} hours of notes · ${rule.definition.timezone}`;
        const recipients = rule.definition.recipient_member_ids.length ? `${rule.definition.recipient_member_ids.length} selected employees` : "All active employees with published shifts";
        return {
            key: `handover:${rule.id}`,
            searchText: [rule.definition.title, rule.definition.instructions, timing, recipients, rule.enabled ? "Enabled" : "Disabled"],
            render: (navigationBlocked: boolean) => renderLibrary ? <div key={`handover:${rule.id}`} className={browserStyles.workflowCard}>
                <button type="button" className={browserStyles.workflowNameCard} aria-label={rule.definition.title}
                    disabled={busy || navigationBlocked} onClick={() => onEdit(rule.id)}>
                    <span className={browserStyles.workflowCardName}>{rule.definition.title}</span>
                    <span className={`${browserStyles.workflowCardSummary} ${builderStyles.librarySummary}`}>{rule.definition.instructions}</span>
                    <span className={reminderStyles.libraryMetadata}>
                        <span>{timing}</span>
                        <span>{recipients}</span>
                        <span className={reminderStyles.status} data-active={rule.enabled}>{rule.enabled ? "On" : "Off"}</span>
                    </span>
                </button>
                <div className={reminderStyles.libraryActions}>
                    <button type="button" role="switch" aria-checked={rule.enabled} aria-label={`Enable ${rule.definition.title}`}
                        disabled={busy || navigationBlocked} onClick={() => { void change(rule, false); }}><span /></button>
                    <Button variant="ghost" size="sm" iconOnly iconShape="square" aria-label={`Delete ${rule.definition.title}`}
                        disabled={busy || navigationBlocked} onClick={() => setDeleting(rule)}><Trash2 size={18} /></Button>
                </div>
            </div> : <article key={`handover:${rule.id}`} className={styles.rule}>
                <div className={styles.heading}><h2>{rule.definition.title}</h2><Button iconOnly size="sm" aria-label={`Delete ${rule.definition.title}`} disabled={busy || navigationBlocked} onClick={() => setDeleting(rule)}><Trash2 size={16} /></Button></div>
                <p>{rule.definition.instructions}</p>
                <p className={styles.hint}>{timing}</p>
                <p className={styles.hint}>{recipients}</p>
                <div className={styles.actions}>
                    <Button size="sm" leftIcon={<Pencil size={15} />} disabled={busy || navigationBlocked} onClick={() => onEdit(rule.id)}>Edit</Button>
                    <Button size="sm" role="switch" aria-checked={rule.enabled} aria-label={`Enable ${rule.definition.title}`} disabled={busy} onClick={() => { void change(rule, false); }}>{rule.enabled ? "Enabled" : "Disabled"}</Button>
                </div>
            </article>
        };
    });
    const deleteModal = deleting && <Modal open onOpenChange={open => { if (!open && !busy) setDeleting(null); }}>
        <Modal.Header title={`Delete ${deleting.definition.title}?`} description="Future handovers from this rule will stop. Previously delivered cards remain in employees’ Workspace." />
        <Modal.Body>{error && <p role="alert" className={styles.error}>{error}</p>}</Modal.Body>
        <Modal.Footer><Button disabled={busy} onClick={() => setDeleting(null)}>Cancel</Button><Button disabled={busy} onClick={() => { void change(deleting, true); }}>Delete</Button></Modal.Footer>
    </Modal>;
    if (renderLibrary) return <section>{renderLibrary({ cards, loading, feedback, navigationBlocked: busy, hasError: Boolean(error) })}{deleteModal}</section>;
    return <section>
        <div className={styles.heading}><h2>Shift handovers</h2><Button leftIcon={<Plus size={16} />} onClick={onCreate}>New workflow</Button></div>
        <p className={styles.hint}>Shared rules follow employees’ published shifts. Handover cards are delivered privately to each employee’s Workspace.</p>
        {feedback}
        {!loading && !error && !items.length && <p>No shift handover rules yet. Create a workflow such as “Send staff a handover before each shift.”</p>}
        <div className={styles.grid}>{cards.map(card => card.render(false))}</div>
        {deleteModal}
    </section>;
}
